import React, { useEffect, useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, MenuItem,
  Divider, CircularProgress, alpha, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import TvIcon from "@mui/icons-material/Tv";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PaidIcon from "@mui/icons-material/Paid";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDeviceQuery,
  useUpdateDeviceMutation,
  useGetPlaylistGroupsQuery,
} from "../../../Api/attoDeskApi";
import { IDevice, IPlaylistGroup } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import PageLoader from "../../../Components/Loader/PageLoader";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";

const DeviceEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const deviceId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading } = useGetDeviceQuery(deviceId);
  const [updateDevice, { isLoading: isSaving }] = useUpdateDeviceMutation();
  const { data: groupsData } = useGetPlaylistGroupsQuery();

  const groups: IPlaylistGroup[] = (Array.isArray(groupsData?.data)
    ? ((groupsData?.data as unknown) as IPlaylistGroup[])
    : []).filter(g => g.is_active);

  const [deviceName, setDeviceName] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [playlistGroupId, setPlaylistGroupId] = useState<number | "">("");

  // Billing — kept as strings so empty input clears the column to null on submit.
  const [monthlyRate, setMonthlyRate] = useState<string>("");
  const [activationDate, setActivationDate] = useState<string>("");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const device = data?.data as unknown as IDevice | undefined;

  useEffect(() => {
    if (device) {
      setDeviceName(device.device_name || "");
      setDeviceCode(device.device_code || "");
      setLocation(device.location || "");
      setPlaylistGroupId(device.playlist_group_id ?? "");
      setMonthlyRate(device.monthly_rate != null ? String(device.monthly_rate) : "");
      setActivationDate(device.activation_date ?? "");
      setSubscriptionStatus(device.subscription_status ?? "");
    }
  }, [device]);

  const isValid = deviceName.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      showErrorMessage("Device name is required");
      return;
    }
    try {
      const body: any = {
        device_name: deviceName.trim(),
        device_code: deviceCode.trim(),
        location: location.trim() || null,
        playlist_group_id: playlistGroupId === "" ? null : Number(playlistGroupId),
      };
      // Only super_admin sees the billing block, but if a value is on screen
      // we always send it so an admin can clear/edit it from their own session.
      if (isSuperAdmin) {
        body.monthly_rate = monthlyRate === "" ? null : Number(monthlyRate);
        body.activation_date = activationDate === "" ? null : activationDate;
        body.subscription_status = subscriptionStatus === "" ? null : subscriptionStatus;
      }
      const res = await updateDevice({ id: deviceId, body }).unwrap();
      if (res.status) {
        showMessage("Device updated");
        navigate("/device/list");
      } else {
        showErrorMessage((res as any).message || "Failed to update device");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to update device");
    }
  };

  if (isLoading || !device) return <PageLoader height="20vh" />;

  const online = device.status === "online";

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton
            onClick={() => navigate("/device/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.3)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <TvIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                Edit Device
              </Typography>
              <Typography fontSize={12} color="rgba(255,255,255,0.45)" sx={{ ml: 1, fontFamily: "monospace" }}>
                {device.device_code}
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              Update device configuration
            </Typography>
          </Box>
          <Button
            variant="outlined" size="small"
            onClick={() => navigate("/device/list")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
              "&:hover": {
                borderColor: "rgba(255,255,255,0.3)",
                bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" disableElevation size="small"
            disabled={!isValid || isSaving} onClick={handleSubmit}
            startIcon={
              isSaving
                ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                : <SaveIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #006064 0%, #00838f 100%)",
                boxShadow: "0 4px 20px rgba(0,131,143,0.5)",
              },
              "&.Mui-disabled": {
                background: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.25)",
              },
            }}
          >
            {isSaving ? "Saving..." : "Update Device"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Device Information"
              accentColor="#3b82f6"
              subtitle="Device name, location, and notes"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device Name *</Typography>
                  <TextField
                    fullWidth value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    sx={{
                      ...signageInputSx,
                      "& .MuiOutlinedInput-root": {
                        ...signageInputSx["& .MuiOutlinedInput-root"],
                        fontSize: 15, fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Location</Typography>
                  <TextField
                    fullWidth size="small"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    sx={signageInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Notes</Typography>
                  <TextField
                    fullWidth size="small" multiline rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={signageInputSx}
                  />
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#00838f" }} />}
              title="Device Details"
              accentColor="#00838f"
              subtitle="Code, status and playlist"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device Code</Typography>
                  <TextField
                    fullWidth size="small"
                    value={deviceCode}
                    onChange={(e) => setDeviceCode(e.target.value)}
                    sx={signageInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Status</Typography>
                  <Box sx={{
                    p: 2, borderRadius: 2.5,
                    bgcolor: online ? alpha("#10b981", 0.06) : alpha("#ef4444", 0.06),
                    border: `1px solid ${online ? alpha("#10b981", 0.18) : alpha("#ef4444", 0.18)}`,
                    display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <Chip
                      size="small"
                      icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}
                      label={online ? "Online" : "Offline"}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        bgcolor: online ? "#ecfdf5" : "#fef2f2",
                        color: online ? "#047857" : "#b91c1c",
                        border: `1px solid ${online ? "#a7f3d0" : "#fecaca"}`,
                        "& .MuiChip-icon": {
                          color: online ? "#10b981" : "#ef4444", ml: 0.75,
                        },
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: "#64748b", ml: "auto" }}>
                      {device.last_seen_at
                        ? new Date(device.last_seen_at).toLocaleString()
                        : "No heartbeat yet"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                    <PlaylistPlayIcon sx={{ fontSize: 14, color: "#00838f" }} />
                    <Typography sx={{ ...signageLabelSx, mb: 0 }}>
                      Assigned Playlist Group
                    </Typography>
                  </Box>
                  <TextField
                    select fullWidth size="small"
                    value={playlistGroupId}
                    onChange={(e) =>
                      setPlaylistGroupId(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={signageInputSx}
                  >
                    <MenuItem value="">No Playlist Group</MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name} · {g.playlists_count ?? 0} playlists · {g.playback_mode}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </SignageSection>

            {isSuperAdmin && (
              <SignageSection
                icon={<PaidIcon sx={{ fontSize: 18, color: "#0891b2" }} />}
                title="Billing"
                accentColor="#0891b2"
                subtitle="Monthly rate, activation date, and subscription status"
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Monthly Rate</Typography>
                    <TextField
                      fullWidth size="small" type="number"
                      value={monthlyRate}
                      onChange={(e) => setMonthlyRate(e.target.value)}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: "0.01" }}
                      sx={signageInputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Activation Date</Typography>
                    <TextField
                      fullWidth size="small" type="date"
                      value={activationDate}
                      onChange={(e) => setActivationDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={signageInputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Subscription Status</Typography>
                    <TextField
                      select fullWidth size="small"
                      value={subscriptionStatus}
                      onChange={(e) => setSubscriptionStatus(e.target.value)}
                      sx={signageInputSx}
                    >
                      <MenuItem value="">— Not set —</MenuItem>
                      <MenuItem value="trial">Trial</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </SignageSection>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DeviceEdit;
